<template>
  <ModalDialog ref="modal"
               height="auto"
               :width="isMobile ? '100%' : '39rem'"
               :showCloseButton="true"
               @close="okClicked">
    <template #heading>
      <div class="header">
        <div class="title">{{ dialogTitle }}</div>
      </div>
    </template>
    <div class="bodyWrapper">
      <div class="bodyContent">
        <h2>Overview</h2>
        <div class="summaryText" v-text="mainSummaryText" />
        <template v-if="hasThreads">
          <h2 class="threadsSection">Threads</h2>
          <div v-for="threadSummary of threadSummaries" :key="threadSummary.parentMessageId" class="threadMessage">
            <div class="threadMessageTitle">
              {{ threadMessageToDisplay(threadSummary) }}
            </div>
            <div class="threadMessageSummary">
              {{ threadSummary.summary }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </ModalDialog>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Ref, ref } from 'vue';

import ModalDialog, { ModalDialogApi } from '@/components/dialogs/ModalDialog.vue';
import BrowserDetection from '@/data/BrowserDetection';
import { ChatSummary } from '@/data/datatypes/ChatSummary';
import { Track } from '@/data/datatypes/Track';

const MAX_WORKSPACE_TITLE_SIZE: number = 25;
const MAX_THREAD_TITLE_SIZE: number = 75;

interface Props {
  track: Track;
}
const props = defineProps<Props>();

interface Emits {
  (e: 'close'): void,
}
const emit = defineEmits<Emits>();

const modal: Ref<ModalDialogApi | null> = ref(null);

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const mainSummaryText: ComputedRef<string> = computed(() => {
  if (props.track.chatSummaries && props.track.chatSummaries.length > 0) {
    for (const chatSummary of props.track.chatSummaries) {
      if (!chatSummary.parentMessageId) {
        return chatSummary.summary;
      }
    }
  }
  return 'Chat summary not available.';
});

const threadSummaries: ComputedRef<ChatSummary[]> = computed(() => {
  const toReturn: ChatSummary[] = [];
  if (props.track.chatSummaries && props.track.chatSummaries.length > 0) {
    for (const chatSummary of props.track.chatSummaries) {
      if (chatSummary.parentMessageId) {
        toReturn.push(chatSummary);
      }
    }
  }
  return toReturn;
});

const hasThreads: ComputedRef<boolean> = computed(() => {
  return threadSummaries.value.length > 0;
});

const dialogTitle: ComputedRef<string> = computed(() => {
  let ellipsis = false;
  if (props.track.title && props.track.title.length > MAX_WORKSPACE_TITLE_SIZE) {
    ellipsis = true;
  }

  let trackTitle = props.track.title?.substring(0, MAX_WORKSPACE_TITLE_SIZE);
  if (ellipsis) {
    trackTitle = trackTitle + '...';
  }

  if (threadSummaries.value.length > 0) {
    return trackTitle + ' message summaries';
  }
  return trackTitle + ' message summary';
});

function okClicked(): void {
  modal.value?.closeDialog();
  emit('close');
}

function threadMessageToDisplay(chatSummary: ChatSummary): string | undefined {
  let ellipsis = false;
  if (chatSummary.parentMessagePlainText && chatSummary.parentMessagePlainText.length > MAX_THREAD_TITLE_SIZE) {
    ellipsis = true;
  }
  let toReturn = chatSummary.parentMessagePlainText?.substring(0, MAX_THREAD_TITLE_SIZE);
  if (ellipsis) {
    toReturn = toReturn + '...';
  }
  return toReturn;
}
</script>

<style scoped lang="scss">
.header {
  font-size: $size-font-header;
  font-weight: $weight-light;
  line-height: 2em;
}
.bodyWrapper {
  width: 100%;
  height: 100%;
  position: relative;
  margin-bottom: 1rem;

  .bodyContent {
    width: 100%;
    height: 100%;
    position: relative;
    h2 {
      margin-bottom: 1rem;
      font-size: 1.25rem;
    }
  }
}
.summaryText {
  white-space: pre-line;
}
.threadsSection {
  margin-top: 1rem;
}
.threadMessage {
  .threadMessageTitle {
    font-weight: bold;
  }
  .threadMessageSummary {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }
}
</style>
